import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FolderOpen, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "./common";
import { resolveMedia } from "../../lib/api";
import { DEFAULT_INDUSTRIES, DEFAULT_SOLUTIONS } from "../../constants/defaultSiteData";

const indSlugMap = {
  "REAL ESTATE": "real-estate",
  "MANUFACTURING & B2B": "manufacturing-b2b",
  "D2C": "d2c",
  "HEALTHCARE": "healthcare",
  "EDUCATION": "education",
  "HOSPITALITY": "hospitality",
  "ENTERTAINMENT": "entertainment",
};

/* WORK / CASE STUDIES */
export function Work({ caseStudies }) {
  const list = caseStudies || [];
  return (
    <section id="work" data-testid="section-work" className="bg-white py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-14">
          <div>
            <SectionLabel>Work</SectionLabel>
            <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-navy sm:text-6xl">
              Selected Case Studies
            </h2>
          </div>
          <Link
            to="/work"
            className="inline-flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:underline"
          >
            View Full Portfolio & Showcase <ArrowRight size={16} />
          </Link>
        </div>
        {list.length === 0 ? (
          <div data-testid="work-empty-state" className="flex flex-col items-center rounded-3xl border border-dashed border-navy/20 bg-[#F8F9FA] py-24 text-center">
            <FolderOpen className="mb-4 text-navy/30" size={48} />
            <p className="font-display text-2xl font-bold uppercase text-navy">Case studies coming soon</p>
            <p className="mt-2 max-w-md px-6 font-body text-sm text-navy/50">
              Real ADMALL projects will appear here. We publish only genuine, results-backed work — never fabricated clients.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {list.map((c, i) => (
              <Reveal key={c.id || i} delay={(i % 2) * 0.08}>
                <article className="group overflow-hidden rounded-2xl border border-navy/10" data-testid={`case-study-${i}`}>
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {c.cover_image ? (
                      <img src={resolveMedia(c.cover_image)} alt={c.title} loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-navy"><FolderOpen className="text-white/30" size={40} /></div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="mb-2 flex items-center gap-3">
                      {c.industry && <span className="rounded-full bg-[#FF5722]/10 px-3 py-1 font-body text-xs font-semibold uppercase tracking-wide text-[#FF5722]">{c.industry}</span>}
                      {c.client && <span className="font-body text-xs font-semibold uppercase tracking-wide text-navy/50">{c.client}</span>}
                    </div>
                    <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-navy md:text-3xl">{c.title}</h3>
                    {c.challenge && <p className="mt-3 font-body text-sm leading-relaxed text-navy/60">{c.challenge}</p>}
                    {c.outcome && (
                      <p className="mt-4 flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-navy">
                        <ArrowUpRight size={18} className="text-[#FF5722]" /> {c.outcome}
                      </p>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* SOLUTIONS / PACKAGES */
export function Solutions({ solutions }) {
  const list = (solutions && solutions.length > 0) ? solutions : DEFAULT_SOLUTIONS;
  return (
    <section id="solutions" data-testid="section-solutions" className="bg-[#F8F9FA] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionLabel>Solutions</SectionLabel>
        <h2 className="mb-14 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-navy sm:text-6xl">
          Ways To Work With ADMALL
        </h2>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((s, i) => (
            <Reveal key={s.id || i} delay={(i % 3) * 0.07}>
              <div data-testid={`solution-card-${i}`}
                className="flex h-full flex-col rounded-2xl border border-navy/10 bg-white p-7 transition-all hover:border-[#FF5722] hover:shadow-xl">
                <span className="h-2 w-2 rounded-full bg-[#FF5722] mb-3 block" />
                <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-navy">{s.title}</h3>
                <p className="mt-2 font-display text-base font-bold uppercase tracking-wide text-navy/70">{s.hook}</p>
                <p className="mt-4 flex-1 font-body text-sm leading-relaxed text-navy/60">{s.desc}</p>
                <div className="mt-6 border-t border-navy/10 pt-5">
                  <p className="font-display text-xl font-extrabold text-navy">{s.price}</p>
                  {s.note && <p className="mt-1 font-body text-xs text-navy/40">{s.note}</p>}
                  <a href="#contact" data-testid={`solution-cta-${i}`}
                    className="mt-4 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:text-[#E64A19] transition">
                    Enquire <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* INDUSTRIES */
export function Industries({ industries }) {
  const [active, setActive] = useState(0);
  const list = (industries && industries.length > 0) ? industries : DEFAULT_INDUSTRIES;
  return (
    <section id="industries" data-testid="section-industries" className="bg-[#0D152A] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-14">
          <div>
            <SectionLabel dark>Industries</SectionLabel>
            <h2 className="max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">
              Built For Businesses That Want To Move.
            </h2>
          </div>
          <Link
            to="/industries"
            className="inline-flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wider text-[#FF5722] hover:underline"
          >
            All Industries Overview <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            {list.map((ind, i) => {
              const targetSlug = indSlugMap[ind.title] || ind.title?.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <div key={ind.id || i} className="border-b border-white/10 py-6">
                  <button
                    data-testid={`industry-item-${i}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className="group block w-full text-left"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <h3 className={`font-display text-3xl font-extrabold uppercase tracking-tight transition-colors sm:text-4xl ${active === i ? "text-[#FF5722]" : "text-white/70 group-hover:text-white"}`}>
                        {ind.title}
                      </h3>
                      <ArrowUpRight className={`shrink-0 transition ${active === i ? "text-[#FF5722]" : "text-white/30"}`} size={24} />
                    </div>
                  </button>
                  <div className={`grid transition-all duration-500 ${active === i ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <p className="font-body text-sm leading-relaxed text-white/60 mb-3">{ind.desc}</p>
                      <Link
                        to={`/industries/${targetSlug}`}
                        className="inline-flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-wider text-[#FF5722] hover:underline"
                      >
                        Explore {ind.title} Solutions <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="sticky top-28 hidden h-[520px] overflow-hidden rounded-3xl lg:block">
            {list.map((ind, i) => (
              <img
                key={ind.id || i}
                src={resolveMedia(ind.image)}
                alt={ind.title}
                loading="lazy"
                className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ${active === i ? "scale-100 opacity-100" : "scale-105 opacity-0"}`}
              />
            ))}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}
